import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Divider, Link, Popover, Text } from '@amzn/storm-ui';
import { userCircleOutline } from '@amzn/storm-ui-icons';
import { CurrencyConfigProvider } from '@amzn/aaccurrency-visualisation-ui';
import { UserInfo } from '@amzn/aac-chrome-internal-api';
import { selectUserInfoFromHeader } from 'src/features/header/store/slice';
import { RootState } from 'src/app/store';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import SignOutLink from './SignOutLink';
import {
  StyledHeading,
  SwitchAccountsLink,
  UserDropdownSectionContainer,
  UserDropdownWrapper,
} from '../styles';
import {
  isStormConfigAvailable,
  isStormConfigDarkModeEnabled,
  isStormConfigFeatureEnabled,
  limitTextLength,
} from 'src/commons/utils';
import { UserDropdownPublisher } from 'src/commons/utils/metrics';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { PAGE_LAYOUT_TYPE } from 'src/app/slice';
import CurrencyVisualisation from '../CurrencyVisualisation/CurrencyVisualisation';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { showInternalView } from 'src/commons/utils/helpers';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { getTaktAttributes } from 'src/commons/utils/logWithTakt';
import { chromeComponents } from 'src/constants';
import ThemeModesControl from 'src/features/header/components/ThemeModesControl/ThemeModesControl';
import { WeblabedFeatures } from 'src/commons/constants';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import UtilityButton from 'src/features/header/components/UtilityMenu/UtilityButton';

const userDropDownTaktId = 'aac-user-name-dropdown';
const switchAccountTaktId = 'aac-switch-account-link';
const notificationSettingsTaktId = 'aac-notification-settings-link';
const userProfileTaktId = 'aac-user-profile-link';

interface UserDropdownProps {
  userInfo: UserInfo;
}

const UserDropdown: React.VFC<UserDropdownProps> = ({
  userInfo: {
    customerInfo,
    signOutInfo,
    languageInfo,
    switchAccountInfo,
    notificationSettingsInfo,
    userSettingsInfo,
  },
}) => {
  const fullName = customerInfo?.fullName;
  const primaryEmail = customerInfo?.primaryEmail;
  const bundle = useSidenavBundle();
  const chromeConfig = useChromeConfigContext();

  const [isOpen, setIsOpen] = useState(false);
  const handleToggle = () => {
    if (isOpen) {
      UserDropdownPublisher.publishCounterMonitor('Close', 1);
    } else {
      UserDropdownPublisher.publishCounterMonitor('Open', 1);
    }
    setIsOpen(!isOpen);
  };
  const handleClose = useCallback(() => setIsOpen(false), [setIsOpen]);

  const currencyVisualisation = chromeConfig?.currencyVisualisation;
  const hasCurrencyVisualisation =
    currencyVisualisation?.useCurrencyVisualisation &&
    chromeConfig.pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS &&
    chromeConfig.pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS_NO_HEADER;
  const hasSwitchAccountInfo = !!(
    switchAccountInfo &&
    switchAccountInfo.label &&
    switchAccountInfo.url
  );
  const themeModesControlFromContext = useIsFeatureEnabled(
    WeblabedFeatures.CHROME_THEME_MODES_CONTROL,
  );
  // Prefer the synchronous StormConfig flag (available at first paint); fall
  // back to the async context read only when the server did not emit StormConfig.
  const isChromeThemeModesControlEnabled = isStormConfigAvailable()
    ? isStormConfigFeatureEnabled(WeblabedFeatures.CHROME_THEME_MODES_CONTROL)
    : themeModesControlFromContext;

  const { config: internalChromeConfig } = useInternalChromeConfigContext();

  const { isInternalView } = internalChromeConfig;
  const isDarkModeEnabled = isStormConfigDarkModeEnabled() === true;
  const isThemeModesControlVisible =
    isChromeThemeModesControlEnabled && !!isDarkModeEnabled;
  const isUserProfileEnabled =
    userSettingsInfo &&
    showInternalView(chromeConfig.pageLayoutType, isInternalView);

  return (
    <Popover
      isOpen={isOpen}
      autoFocus={true}
      trigger={
        <UtilityButton
          icon={userCircleOutline}
          label={bundle.getMessage('sidenav-your-preferences-label')}
          onClick={handleToggle}
          aria-haspopup={true}
          aria-expanded={isOpen}
          data-ccx-e2e-id={userDropDownTaktId}
          {...getTaktAttributes(userDropDownTaktId, chromeComponents.HEADER)}
        />
      }
      position="bottom"
      align="end"
      strategy="fixed"
      onCloseButtonClick={handleClose}
      spacing={0}
      padding="none"
      data-ccx-e2e-id="aac-user-name-dropdown-wrapper"
    >
      <UserDropdownWrapper>
        {(!!fullName || !!primaryEmail || hasSwitchAccountInfo) && (
          <>
            <UserDropdownSectionContainer>
              {fullName && (
                <StyledHeading renderAs="h6" typeface="display">
                  {limitTextLength(fullName, 42)}
                </StyledHeading>
              )}
              {primaryEmail && (
                <Text color="tertiary" size="small">
                  {limitTextLength(primaryEmail, 70)}
                </Text>
              )}
              {hasSwitchAccountInfo && (
                <SwitchAccountsLink
                  size="small"
                  data-ccx-e2e-id={switchAccountTaktId}
                  href={switchAccountInfo.url}
                  withMargin={!!fullName || !!primaryEmail}
                  {...getTaktAttributes(
                    switchAccountTaktId,
                    chromeComponents.HEADER,
                  )}
                >
                  {switchAccountInfo.label}
                </SwitchAccountsLink>
              )}
            </UserDropdownSectionContainer>
            <Divider marginBlockStart="small" marginBlockEnd="small" />
          </>
        )}
        <UserDropdownSectionContainer>
          <LanguageSwitcher
            label={languageInfo.label}
            currentLocale={languageInfo.currentLocale}
            languageOptions={languageInfo.languageOptions}
            data-ccx-e2e-id="aac-language-switcher"
            changeLanguageText={languageInfo.changeLanguageText}
          />
        </UserDropdownSectionContainer>
        <Divider marginBlockStart="small" marginBlockEnd="small" />
        {hasCurrencyVisualisation && (
          <>
            <UserDropdownSectionContainer>
              <CurrencyConfigProvider>
                <CurrencyVisualisation />
              </CurrencyConfigProvider>
            </UserDropdownSectionContainer>
            <Divider marginBlockStart="small" marginBlockEnd="small" />
          </>
        )}
        {isThemeModesControlVisible && (
          <>
            <UserDropdownSectionContainer>
              <ThemeModesControl />
            </UserDropdownSectionContainer>
            <Divider marginBlockStart="small" marginBlockEnd="small" />
          </>
        )}
        {notificationSettingsInfo && (
          <>
            <UserDropdownSectionContainer>
              <Link
                size="small"
                data-ccx-e2e-id={notificationSettingsTaktId}
                href={notificationSettingsInfo.url}
                {...getTaktAttributes(
                  notificationSettingsTaktId,
                  chromeComponents.HEADER,
                )}
              >
                {notificationSettingsInfo.label}
              </Link>
            </UserDropdownSectionContainer>
            <Divider marginBlockStart="small" marginBlockEnd="small" />
          </>
        )}
        {isUserProfileEnabled && (
          <>
            <UserDropdownSectionContainer>
              <Link
                size="small"
                data-ccx-e2e-id={userProfileTaktId}
                href={userSettingsInfo.url}
                {...getTaktAttributes(
                  userProfileTaktId,
                  chromeComponents.HEADER,
                )}
              >
                {userSettingsInfo.label}
              </Link>
            </UserDropdownSectionContainer>
            <Divider marginBlockStart="small" marginBlockEnd="small" />
          </>
        )}
        <UserDropdownSectionContainer>
          <SignOutLink signOutInfo={signOutInfo} />
        </UserDropdownSectionContainer>
      </UserDropdownWrapper>
    </Popover>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    userInfo: selectUserInfoFromHeader(state),
  };
};

export default connect(mapStateToProps)(UserDropdown);
